import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQPage = () => {
  const faqCategories = [
    {
      title: 'Membership',
      faqs: [
        {
          question: 'How do I become a member of the DCCF?',
          answer: 'You can apply for membership by visiting our Membership page and filling out the application form. Our team will review your application and contact you within 5 business days.'
        },
        {
          question: 'What are the membership fees?',
          answer: 'Membership fees vary based on the type of membership. We offer individual, small business, and corporate tiers. Contact us for detailed pricing information.'
        },
        {
          question: 'What benefits do members receive?',
          answer: 'Members enjoy networking opportunities, business visibility through our directory, exclusive event access, marketing exposure, professional development workshops, and connections to resources in both Florida and the Dominican Republic.'
        },
        {
          question: 'Can non-Dominicans join the chamber?',
          answer: 'Yes! While our focus is on the Dominican business community, we welcome all entrepreneurs and professionals who want to be part of our network.'
        }
      ]
    },
    {
      title: 'Events',
      faqs: [
        {
          question: 'How can I attend DCCF events?',
          answer: 'Members get free or discounted access to most events. Non-members can attend select events by purchasing tickets through our Events page.'
        },
        {
          question: 'Can I host an event with the DCCF?',
          answer: 'Yes! We partner with members and sponsors to host events. Contact us through our Marketing Request page to discuss collaboration opportunities.'
        },
        {
          question: 'Are events virtual or in-person?',
          answer: 'We offer both virtual and in-person events. Check our Events calendar for specific event formats and locations.'
        }
      ]
    },
    {
      title: 'Marketing & Brand',
      faqs: [
        {
          question: 'How can I request marketing support?',
          answer: 'Visit our Marketing Request page to submit a request for event marketing, promotions, or social media content. Our team will review and respond within 3-5 business days.'
        },
        {
          question: 'Can I use the DCCF logo?',
          answer: 'Yes, but you must first submit a Brand Request through our Brand Resources page. All logo usage must be approved and follow our brand guidelines.'
        },
        {
          question: 'How can I get featured on DCCF platforms?',
          answer: 'Members are eligible for spotlights, featured listings, and social media features. Contact our marketing team to discuss featuring opportunities.'
        }
      ]
    },
    {
      title: 'General',
      faqs: [
        {
          question: 'Where is the DCCF located?',
          answer: 'Our office is located at 28 Broadway Ave, Suite 209, Kissimmee, FL 34741. We serve the entire state of Florida.'
        },
        {
          question: 'How can I contact the chamber?',
          answer: 'You can reach us by phone at 1-866-310-3380, email at info@dominicanchamberfl.org, or through our Contact page.'
        },
        {
          question: 'Is the DCCF a nonprofit organization?',
          answer: 'Yes, the Dominican Chamber of Commerce of Florida is a registered 501(c)(6) nonprofit organization.'
        },
        {
          question: 'How can I volunteer or get involved?',
          answer: 'We welcome volunteers and committee members. Contact us through our website to learn about current opportunities.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-16 sm:pt-20">
      {/* Hero Section */}
      <section className="relative py-14 sm:py-20 bg-[#002249]" data-testid="faq-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block">
              FAQ
            </span>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5">
              Frequently Asked Questions
            </h1>
            <p className="text-white/75 text-sm sm:text-base lg:text-lg leading-relaxed">
              Find answers to common questions about the Dominican Chamber of Commerce of Florida.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="mb-12 last:mb-0"
                data-testid={`faq-category-${categoryIndex}`}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-[#002249] rounded-sm flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#002249]">{category.title}</h2>
                </div>

                <Accordion type="single" collapsible className="space-y-3">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem
                      key={faqIndex}
                      value={`${categoryIndex}-${faqIndex}`}
                      className="bg-slate-50 border border-slate-200 rounded-sm px-6"
                      data-testid={`faq-${categoryIndex}-${faqIndex}`}
                    >
                      <AccordionTrigger className="text-left font-semibold text-[#002249] hover:text-[#E63946] py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="section-padding bg-slate-50" data-testid="faq-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-[#002249] mb-4">
              Still Have Questions?
            </h2>
            <p className="text-slate-600 mb-8">
              Can't find the answer you're looking for? Our team is here to help.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#002249] hover:bg-[#0a3570] rounded-sm" data-testid="faq-contact-btn">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
